import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Sparkles, Bot, User, Trash2, MessagesSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

const SUGGESTIONS = [
  'Summarise the scope of work',
  'What is explicitly excluded?',
  'What are the completion timelines?',
  'Which quality standards must we meet?',
];

export const DocumentChat = ({ projectId, hasDocuments }) => {
  const [enabled, setEnabled] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const busy = messages.some((m) => m.pending);

  const loadThread = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/chat`);
      setEnabled(r.data.enabled);
      setMessages(r.data.messages || []);
      return r.data.processing;
    } catch { return false; }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const processing = await loadThread();
      if (!processing && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 3000);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const processing = await loadThread();
      setLoading(false);
      if (processing) startPolling();
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    try {
      const r = await api.post(`/projects/${projectId}/chat`, { message: q });
      setMessages(r.data.messages || []);   // includes a pending assistant placeholder
      startPolling();                        // poll until the answer resolves
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The assistant could not answer right now.');
    }
  };

  const clear = async () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    try { await api.delete(`/projects/${projectId}/chat`); setMessages([]); toast('Conversation cleared'); }
    catch { toast.error('Could not clear'); }
  };

  if (loading) return <Card className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading assistant…</Card>;

  if (!enabled) {
    return (
      <Card className="p-8 text-center" data-testid="chat-disabled">
        <MessagesSquare className="h-8 w-8 text-slate-300 mx-auto" />
        <div className="text-sm font-semibold text-slate-700 mt-3">Document assistant is turned off</div>
        <p className="text-xs text-slate-500 mt-1">An admin can re-enable it once AI budget allows.</p>
      </Card>
    );
  }

  if (!hasDocuments) {
    return (
      <Card className="p-8 text-center" data-testid="chat-no-docs">
        <MessagesSquare className="h-8 w-8 text-slate-300 mx-auto" />
        <div className="text-sm font-semibold text-slate-700 mt-3">No readable documents yet</div>
        <p className="text-xs text-slate-500 mt-1">Add native-text PDFs (or paste a scope) so the assistant has something to read.</p>
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden flex flex-col" style={{ height: '600px' }} data-testid="document-chat">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Bot className="h-4 w-4 text-brand" />Ask the documents
        </div>
        {messages.length > 0 && (
          <button onClick={clear} className="text-xs text-slate-400 hover:text-danger-600 flex items-center gap-1" data-testid="chat-clear">
            <Trash2 className="h-3.5 w-3.5" />Clear
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-slate-50/40">
        {messages.length === 0 && (
          <div className="text-center py-6" data-testid="chat-empty">
            <Sparkles className="h-7 w-7 text-brand/70 mx-auto" />
            <p className="text-sm text-slate-600 mt-2 font-medium">Ask anything about this project’s documents</p>
            <p className="text-xs text-slate-400 mt-0.5">Answers are grounded only in what you uploaded.</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="chat-suggestion"
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-brand/50 hover:text-brand transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          m.pending ? (
            <div key={i} className="flex gap-2.5" data-testid="chat-thinking">
              <div className="rounded-full p-1.5 h-7 w-7 shrink-0 flex items-center justify-center bg-white border border-slate-200 text-brand"><Bot className="h-3.5 w-3.5" /></div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3.5 py-3 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          ) : (
            <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${m.role}`}>
              <div className={`rounded-full p-1.5 h-7 w-7 shrink-0 flex items-center justify-center ${m.role === 'user' ? 'bg-brand text-white' : (m.error ? 'bg-amber-50 border border-amber-200 text-amber-600' : 'bg-white border border-slate-200 text-brand')}`}>
                {m.role === 'user' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
              </div>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user' ? 'bg-brand text-white rounded-tr-sm' : (m.error ? 'bg-amber-50 border border-amber-200 text-amber-800 rounded-tl-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-sm')}`}>
                {m.content}
              </div>
            </div>
          )
        ))}
      </div>

      <div className="border-t border-slate-200 p-3 flex items-center gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask about scope, exclusions, timelines, specs…"
          disabled={busy}
          data-testid="chat-input"
        />
        <Button onClick={() => send()} disabled={busy || !input.trim()} className="bg-brand hover:bg-brand-700 shrink-0" data-testid="chat-send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </Card>
  );
};
